import { BusinessUnitKey, type BusinessUnitRuntime } from '@/modules/bu/registry.base';
import FlokeeCheckoutSuccessModal from '@/modules/flokee/components/cart/FlokeeCheckoutSuccessModal.vue';
import PlanSubscriptionApprovalModal from '@/modules/flokee/components/plan-subscription-approval/PlanSubscriptionApprovalModal.vue';
import ProductSuggestionsDrawer from '@/modules/flokee/components/product/ProductSuggestionsDrawer.vue';
import { useTiendanubeInstallationStore } from '@/modules/flokee/integrations/tiendanube/installation.auth';
import { openAuthenticatedTiendanubeAuthorization } from '@/modules/flokee/integrations/tiendanube/tiendanubeAuthorization.utils';
import {
  EcommerceIntegrationStatus,
  EcommerceStatus,
} from '@/modules/flokee/interfaces/ecommerce.interface';
import { ecommerceMiddlewares } from '@/modules/flokee/middlewares/ecommerce.middlewares';
import { useEcommerceSuccessCalculator } from '@/modules/flokee/composables/useEcommerceSuccessCalculator';
import { useEcommerceStore } from '@/modules/flokee/stores/ecommerce.store';
import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useNoticeStore } from '@/stores/notice.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { useUiStore } from '@/stores/ui.store';
import { notice } from '@/utils/notice.utils';
import { normalizeBusinessUnit, summaryOfOrder } from '@/utils/bu.utils';
import { isAdminRole } from '@/utils/role.utils';
import { toast } from '@/utils/toast.utils';

const INTEGRATION_FAILED_NOTICE = 'ecommerce-integration-failed';
const INTEGRATION_INACTIVE_NOTICE = 'ecommerce-integration-inactive';

function showIntegrationNotice(i18n_key: string) {
  const { t } = i18n.global as any;
  notice.error(t(`${i18n_key}.text`), {
    id: INTEGRATION_FAILED_NOTICE,
    dismissable: false,
    actions: [
      {
        text: t(`${i18n_key}.button`),
        onClick: async () => {
          await openAuthenticatedTiendanubeAuthorization('_blank');
        },
      },
      {
        text: t(`${i18n_key}.button_2`),
        onClick: async () => {
          window.open(
            `https://help.flokee.ai/conceptos-basicos/integraciones/tiendanube#qué-sucede-si-la-integración-falla`,
            '_blank',
          );
        },
      },
    ],
  });
}

// Absence needs no handling: cxm's GetOrderSummaryAction throws without a PACKAGE plan and
// OrderSummaryController answers 404, so a 200 always names the product. Measured on release 144:
// 0 NULL solution_id among 1570 plans, 1485 PACKAGE plans and 9759 order_plan rows, 0 of 92
// solutions without business_unit, 0 orphan solution_id, 0 order_plan rows without a plan.
// The ecommerce store survives a switch to a Cleepo order and a domain outranks the order in the
// resolver, so neither an ecommerce nor the resolved BU proves this is a Flokee order.
const isFlokeeOrder = () =>
  normalizeBusinessUnit(
    summaryOfOrder(useOrderSummaryStore().orderSummary, useAuthStore().user?.current_order_id)
      ?.business_unit,
  ) === BusinessUnitKey.Flokee;

// Discovered by `src/modules/bu/registry.runtime.ts`.
export const key = BusinessUnitKey.Flokee;

const runtime: BusinessUnitRuntime = {
  init: () => useEcommerceStore().init(),
  clear: () => useEcommerceStore().clear(),
  resolveEntryRoute() {
    const authStore = useAuthStore();
    const uiStore = useUiStore();
    const ecommerceStore = useEcommerceStore();
    const { t } = i18n.global as any;

    return {
      validateAccount: async () => {
        const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
        if (!authStore.user?.current_account_id) {
          if (isAdminRole(authStore.user)) {
            return 'AdminSelectAccountView';
          }
          return hasAccounts ? 'SelectAccountView' : 'WizardCreateAccountView';
        }

        if (!authStore.currentAccount?.active) {
          return hasAccounts ? 'SelectAccountView' : 'WizardCreateAccountView';
        }
      },
      handleNoCurrentOrder: async () => {
        if (isAdminRole(authStore.user)) {
          return 'AdminSelectAccountView';
        }

        const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
        if (hasAccounts) {
          return 'SelectAccountView';
        }

        const hasEcommerces = ecommerceStore.ecommerces.length > 0;
        if (!hasEcommerces) {
          return 'WizardCreateEcommerceView';
        }

        const activeEcommerce = ecommerceStore.ecommerces.find(
          (e) => e.status === EcommerceStatus.ACTIVE,
        );

        try {
          if (activeEcommerce) {
            await ecommerceStore.setSelectedEcommerce(activeEcommerce.id);
          } else {
            await ecommerceStore.setSelectedEcommerce(ecommerceStore.ecommerces[0].id);
          }
        } catch (error) {
          toast.error({ message: t('notifications.account_not_configured_error') });
          uiStore.openSupportDrawer();
          return 'AuthLoginView';
        }
      },
      validateIntegration: async () => {
        if (ecommerceStore.currentEcommerce && !ecommerceStore.currentEcommerce?.integration) {
          if (isAdminRole(authStore.user)) {
            toast.error({ message: t('notifications.ecommerce_integration_not_set_error') });
            return 'AdminSelectAccountView';
          }
          return 'WizardSelectEcommerceProviderView';
        }
      },
    };
  },
  currentEntityId: () => useEcommerceStore().currentEcommerce?.id,
  checkNotices() {
    const noticeStore = useNoticeStore();
    const status = useEcommerceStore().currentEcommerce?.integration?.status;

    // REAUTHORIZATION_REQUIRED is deliberately not among these: both notices are Tiendanube's,
    // so adding the Woo state would tell a WooCommerce merchant to re-authorize with Tiendanube.
    // Lifting it needs provider-aware notices, which is its own ticket.
    noticeStore.handleNotice(
      status === EcommerceIntegrationStatus.FAILED,
      INTEGRATION_FAILED_NOTICE,
      () => showIntegrationNotice('notices.ecommerce_integration_failed_tiendanube'),
    );
    noticeStore.handleNotice(
      status === EcommerceIntegrationStatus.INACTIVE,
      INTEGRATION_INACTIVE_NOTICE,
      () => showIntegrationNotice('notices.ecommerce_integration_inactive_tiendanube'),
    );
  },
  postAuthenticationRouteName: (default_route_name) =>
    useTiendanubeInstallationStore().getPostAuthenticationRouteName(default_route_name),
  globalComponents: [PlanSubscriptionApprovalModal, ProductSuggestionsDrawer],
  cartGuard: (to, from, next) =>
    ecommerceMiddlewares.showPlanSubscriptionApprovalModal(to, from, next),
  cartContext() {
    if (!isFlokeeOrder()) return {};
    const ecommerce = useEcommerceStore().currentEcommerce;
    return { ecommerce_id: ecommerce?.id, ecommerce_name: ecommerce?.name };
  },
  checkoutSuccessComponent: FlokeeCheckoutSuccessModal,
  onCheckoutCompleted: () => useEcommerceStore().getEcommerces(),
  usePlanChangeOptions(options) {
    const ecommerceStore = useEcommerceStore();
    // Runs for every product: the order gate, not an empty store, keeps this query off elsewhere.
    const { data: successCalculator, isLoading } = useEcommerceSuccessCalculator(
      () => (isFlokeeOrder() ? ecommerceStore.currentEcommerce?.id : undefined) as number,
    );
    const successProbabilityThreshold = Number(
      import.meta.env.VITE_APP_FLOKEE_SUCCESS_PROBABILITY_THRESHOLD,
    );

    const lowestNonCustomOptionId = computed(() => {
      const candidates = options.value.filter((option) => !option.plan?.custom && option.plan);
      if (candidates.length === 0) return undefined;

      return candidates.reduce((lowest_option, current_option) => {
        return Number(current_option.plan?.price) < Number(lowest_option.plan?.price)
          ? current_option
          : lowest_option;
      }).id;
    });

    return {
      options: computed(() =>
        successCalculator.value?.probability &&
        successCalculator.value?.probability <= successProbabilityThreshold
          ? options.value.filter((option) => option.id !== lowestNonCustomOptionId.value)
          : options.value,
      ),
      isLoading,
    };
  },
};

export default runtime;
